import React from 'react'
import { Table, Button, Tag, Space } from 'antd'
import { EyeOutlined, DollarOutlined } from '@ant-design/icons'
import type { ColumnsType } from 'antd/es/table'
import type { Settlement } from '../types/settlement.types'
import { formatINR } from '../../../shared/utils/formatters'

interface SettlementsTableProps {
    settlements: Settlement[]
    onRowClick: (settlement: Settlement) => void
    onProcessClick: (settlement: Settlement) => void
}

export const SettlementsTable: React.FC<SettlementsTableProps> = ({
    settlements,
    onRowClick,
    onProcessClick
}) => {
    const columns: ColumnsType<Settlement> = [
        {
            title: 'Settlement ID',
            dataIndex: 'id',
            key: 'id',
            render: (id: string) => <strong>{id}</strong>,
        },
        {
            title: 'Merchant',
            dataIndex: 'merchant',
            key: 'merchant',
        },
        {
            title: 'Orders',
            dataIndex: 'ordersCount',
            key: 'ordersCount',
        },
        {
            title: 'Total Amount',
            dataIndex: 'totalAmount',
            key: 'totalAmount',
            render: (amount: number | string) => formatINR(amount),
        },
        {
            title: 'Platform Fee',
            dataIndex: 'platformFee',
            key: 'platformFee',
            render: (fee: number | string) => formatINR(fee),
        },
        {
            title: 'Net Amount',
            dataIndex: 'netAmount',
            key: 'netAmount',
            render: (amount: number | string) => <strong>{formatINR(amount)}</strong>,
        },
        {
            title: 'Status',
            dataIndex: 'status',
            key: 'status',
            render: (status: string) => {
                const colors = {
                    pending: 'orange',
                    processing: 'blue',
                    completed: 'green',
                    failed: 'red',
                }
                return <Tag color={colors[status as keyof typeof colors]}>{status.toUpperCase()}</Tag>
            },
        },
        {
            title: 'Scheduled',
            dataIndex: 'scheduledDate',
            key: 'scheduledDate',
        },
        {
            title: 'Actions',
            key: 'actions',
            render: (_, record) => (
                <Space>
                    <Button
                        type="link"
                        icon={<EyeOutlined />}
                        onClick={() => onRowClick(record)}
                    >
                        View
                    </Button>
                    <Button
                        type="primary"
                        size="small"
                        icon={<DollarOutlined />}
                        disabled={record.status !== 'pending'}
                        onClick={(e) => {
                            e.stopPropagation()
                            onProcessClick(record)
                        }}
                    >
                        Process
                    </Button>
                </Space>
            ),
        },
    ]

    return (
        <Table
            columns={columns}
            dataSource={settlements}
            rowKey="id"
            onRow={(record) => ({
                onClick: () => onRowClick(record),
                style: { cursor: 'pointer' },
            })}
            pagination={{
                total: settlements.length,
                pageSize: 10,
                showSizeChanger: true,
                showTotal: (total) => `Total ${total} settlements`,
            }}
        />
    )
}
